import { useEffect, useState } from "react";
import { GhostButton, PageHero, PrimaryButton, Reveal, SectionTag } from "@/components/ui-bits";
import { ENGAGEMENT_MODELS, IMAGES, TAGLINE } from "@/content/site";
import api from "@/lib/api";

const VALUES = [
  { title: "Engineering First", desc: "We are engineers building for engineers. Decisions are made on technical merit, and craft is non-negotiable." },
  { title: "Clarity Over Hype", desc: "We say exactly what we deliver, with what technologies, and what outcomes to expect. No vague promises." },
  { title: "Own the Outcome", desc: "From architecture to production support, we stay accountable for what we build long after launch." },
  { title: "Build to Last", desc: "Maintainable code, documented systems and clean handover — software your teams can evolve without us." },
];

const Company = () => {
  const [leadership, setLeadership] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/leadership").then((r) => setLeadership(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Company"
        lines={["A Technology Company", "Built by Engineers."]}
        sub="TARK — Tark Innovation Private Limited — is a global technology engineering company serving premium, high-value businesses across industries and international markets with cloud, DevOps, data & AI, software development and managed technology services."
        image={IMAGES.engineering}
      >
        <PrimaryButton to="/start-a-project" testId="company-talk-expert">Talk to an Expert</PrimaryButton>
        <GhostButton to="/careers" testId="company-careers">Work at TARK</GhostButton>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10" data-testid="company-values">
        <Reveal>
          <SectionTag>{TAGLINE}</SectionTag>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            How we work
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.07}>
              <div className="card-glow h-full rounded-2xl border border-tark-line bg-tark-card p-7">
                <span className="font-mono text-xs text-tark-cyan">0{i + 1}</span>
                <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-tark-mut">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-tark-line bg-tark-surface/40 py-24" data-testid="company-engagement">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionTag>Engagement Models</SectionTag>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">Work With TARK Your Way</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ENGAGEMENT_MODELS.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-tark-line bg-tark-bg p-7">
                  <span className="font-mono text-xs text-tark-cyan">0{i + 1}</span>
                  <h3 className="mt-4 font-display text-base font-bold tracking-tight">{m.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-tark-mut">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {leadership.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="company-leadership">
          <Reveal>
            <SectionTag>Leadership</SectionTag>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">The people behind TARK</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {leadership.map((person) => (
              <Reveal key={person.id}>
                <div className="h-full rounded-2xl border border-tark-line bg-tark-card p-7" data-testid={`leader-${person.id}`}>
                  {person.photo_url && (
                    <img src={person.photo_url} alt={person.name} className="h-20 w-20 rounded-xl border border-tark-line object-cover" />
                  )}
                  <h3 className="mt-5 font-display text-lg font-bold">{person.name}</h3>
                  <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-tark-cyan">{person.role}</p>
                  {person.bio && <p className="mt-4 text-sm leading-relaxed text-tark-mut">{person.bio}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10">
        <Reveal className="rounded-2xl border border-tark-blue/40 bg-gradient-to-br from-tark-card to-[#0A1A33] p-10 text-center sm:p-14">
          <SectionTag>Get in Touch</SectionTag>
          <h2 className="mt-4 font-display text-2xl font-bold sm:text-3xl">Talk to TARK</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-tark-mut">
            Tell us about your technology objectives and we will take it from there.
          </p>
          {settings?.contact_email && (
            <a href={`mailto:${settings.contact_email}`} data-testid="company-contact-email"
               className="mt-5 inline-block font-mono text-sm text-tark-cyan hover:underline">
              {settings.contact_email}
            </a>
          )}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <PrimaryButton to="/start-a-project" testId="company-start-project">Start a Project</PrimaryButton>
            <GhostButton to="/careers" testId="company-join">Join the Team</GhostButton>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Company;
